/**
 * Hooks for the traveler profile + per-feature toggle system.
 *
 * `useTravelUserProfile` exposes the profile form state and a save mutation.
 * `useTravelFeature` reads one feature flag and returns a toggle helper.
 * Both share a single `profile` query — the profile endpoint already returns
 * the effective feature map, so we don't need a second network round-trip.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { travelApi } from '../api';
import type { TravelerProfileFields, TravelUserProfileResponse } from '../api';
import { queryKeys } from '@/lib/query-keys';

export function useTravelUserProfile() {
  const queryClient = useQueryClient();
  const key = queryKeys.travel.profile();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: key,
    queryFn: () => travelApi.getTravelUserProfile(),
  });

  const updateProfileMut = useMutation({
    mutationFn: (patch: Partial<TravelerProfileFields>) => travelApi.updateTravelUserProfile(patch),
    onSuccess: (res) => {
      queryClient.setQueryData<TravelUserProfileResponse | undefined>(key, (prev) =>
        prev
          ? { ...prev, profile: res.profile, updatedAt: res.updatedAt }
          : { profile: res.profile, features: {}, updatedAt: res.updatedAt },
      );
    },
  });

  return {
    profile: data?.profile ?? ({} as TravelerProfileFields),
    features: data?.features ?? ({} as Record<string, boolean>),
    updatedAt: data?.updatedAt,
    loading: isLoading,
    error: error instanceof Error ? error.message : null,
    refresh: refetch,
    updateProfile: (patch: Partial<TravelerProfileFields>) => updateProfileMut.mutateAsync(patch),
    isSaving: updateProfileMut.isPending,
  };
}

/**
 * Subscribes to a single feature flag. Missing keys default to `true` so that
 * newly-introduced features are opt-out — users see them automatically until
 * they turn them off.
 */
export function useTravelFeature(key: string) {
  const queryClient = useQueryClient();
  const profileKey = queryKeys.travel.profile();

  const { data } = useQuery({
    queryKey: profileKey,
    queryFn: () => travelApi.getTravelUserProfile(),
  });

  const features = data?.features ?? {};
  const enabled = features[key] !== false;

  const toggleMut = useMutation({
    mutationFn: (next: boolean) => travelApi.setTravelFeature(key, next),
    onSuccess: (res) => {
      queryClient.setQueryData<TravelUserProfileResponse | undefined>(profileKey, (prev) =>
        prev ? { ...prev, features: res.features } : prev,
      );
    },
  });

  return {
    enabled,
    toggle: (next: boolean) => toggleMut.mutateAsync(next),
    isToggling: toggleMut.isPending,
  };
}
